import axios, { type AxiosError, type AxiosResponse } from 'axios'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import useSWRInfinite from 'swr/infinite'
import { useAuthContext } from '@/providers/Global'
import type { TPaginateResults } from '@/utils/types'

import type {
  IHUserSWRAxiosInfiniteResult,
  THUserSWRAxiosInfiniteConfig,
  THUserSWRAxiosInfiniteKey,
} from './useSWRAxiosInfinite'

//  ///////////////////////
//  MARK: TYPES
export interface IHUserSWRAxiosInfinitePaginationResult<
  T extends object,
  S extends TPaginateResults<T> = TPaginateResults<T>,
> extends Omit<IHUserSWRAxiosInfiniteResult<S>, 'type'> {
  type: 'InfinitePagination'

  isLoadingInitialData: boolean
  isLoadingMore: boolean

  isEmpty: boolean
  isReachingEnd: boolean
  isRefreshing: boolean

  rows?: T[] | undefined

  count: number
  totalCount: number

  loadMore: () => Promise<AxiosResponse<S>[] | undefined>
  loadMoreVoid: () => void

  refresh: () => void

  clear: () => void
}

//  ///////////////////////
//  MARK: EXPORTS
export function useSWRAxiosInfinitePagination<
  T extends object,
  S extends TPaginateResults<T> = TPaginateResults<T>,
>(
  limit: number,
  key: THUserSWRAxiosInfiniteKey<S>,
  fetchAll?: boolean,
  config?: THUserSWRAxiosInfiniteConfig<S>,
  block?: boolean,
): IHUserSWRAxiosInfinitePaginationResult<T, S> {
  const Auth = useAuthContext()

  const swrConfig = useMemo<THUserSWRAxiosInfiniteConfig<S>>(() => {
    const value: THUserSWRAxiosInfiniteConfig<S> = { ...config }

    if (block) {
      value.refreshInterval = 0
      value.refreshWhenHidden = false
      value.refreshWhenOffline = false
      value.revalidateOnReconnect = false
      value.revalidateOnFocus = false
    }

    return value
  }, [block, config])

  const hook = useSWRInfinite<AxiosResponse<S>, AxiosError>(
    (index, previousPageData: AxiosResponse<S> | null) => {
      const result = key(index, previousPageData)

      if (!result) {
        return null
      }

      return Array.isArray(result)
        ? [...result, Auth.active!.username]
        : [result, Auth.active!.username]
    },
    (args) => axios.get<S>(args[0] as string),
    swrConfig,
  )

  const isLoadingInitialData = useMemo<boolean>(
    () => !hook.data && !hook.error,
    [hook.data, hook.error],
  )

  const isLoadingMore = useMemo<boolean>(
    () =>
      isLoadingInitialData ||
      (hook.size > 0 && !!hook.data && hook.data[hook.size - 1] === undefined),
    [hook.data, hook.size, isLoadingInitialData],
  )

  const isEmpty = useMemo<boolean>(() => hook.data?.[0]?.data.data.length === 0, [hook.data])

  const isReachingEnd = useMemo<boolean>(
    () => isEmpty || (!!hook.data && hook.data.at(-1)!.data.data.length < limit),
    [hook.data, isEmpty, limit],
  )

  const isRefreshing = useMemo<boolean>(
    () => hook.isValidating && !!hook.data && hook.data.length === hook.size,
    [hook.data, hook.isValidating, hook.size],
  )

  const rows = useMemo<IHUserSWRAxiosInfinitePaginationResult<T, S>['rows']>(() => {
    if (hook.data) {
      const value: T[] = []

      for (const row of hook.data) {
        value.push(...row.data.data)
      }

      return value
    }

    return undefined
  }, [hook.data])

  const count = useMemo<number>(() => {
    if (!hook.data || hook.data.length === 0) {
      return 0
    }

    return hook.data.reduce((a, e) => a + e.data.data.length, 0)
  }, [hook.data])

  const totalCount = useMemo<number>(() => {
    if (!hook.data || hook.data.length === 0) {
      return 0
    }

    return hook.data[0].data.totalCount
  }, [hook.data])

  const loadMore = useCallback<IHUserSWRAxiosInfinitePaginationResult<T, S>['loadMore']>(
    () => hook.setSize((v) => v + 1),
    [hook],
  )

  const totalCountRef = useRef(0)
  const limitRef = useRef(0)

  useEffect(() => {
    const maxSize = totalCount > 0 ? Math.ceil(totalCount / limit) : 1

    if (fetchAll && (totalCount !== totalCountRef.current || limit !== limitRef.current)) {
      totalCountRef.current = totalCount
      limitRef.current = limit

      hook.setSize(maxSize)
    }

    // Do not reload on "hook"
  }, [fetchAll, limit, totalCount, hook.setSize])

  const loadMoreVoid = useCallback<
    IHUserSWRAxiosInfinitePaginationResult<T, S>['loadMoreVoid']
  >(() => {
    loadMore()
  }, [loadMore])

  const refresh = useCallback<IHUserSWRAxiosInfinitePaginationResult<T, S>['refresh']>(() => {
    hook.mutate()
  }, [hook])

  const clear = useCallback<IHUserSWRAxiosInfinitePaginationResult<T, S>['clear']>(() => {
    hook.setSize(1)
  }, [hook])

  return useMemo<IHUserSWRAxiosInfinitePaginationResult<T, S>>(
    () => ({
      type: 'InfinitePagination',
      ...hook,
      isLoading: isLoadingInitialData,
      isLoadingInitialData,
      isLoadingMore,
      isEmpty,
      isReachingEnd,
      isRefreshing,
      rows,
      count,
      totalCount,
      loadMore,
      loadMoreVoid,
      refresh,
      clear,
    }),
    [
      hook,
      isLoadingInitialData,
      isLoadingMore,
      isEmpty,
      isReachingEnd,
      isRefreshing,
      rows,
      count,
      totalCount,
      loadMore,
      loadMoreVoid,
      refresh,
      clear,
    ],
  )
}
